import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import apiClient, { getErrorMessage } from '../../api/client';
import { useToast } from '../../hooks/useToast';
import { FiPlus, FiUser, FiEdit2, FiTrash2, FiSave, FiX, FiFileText, FiCheckSquare, FiLink } from 'react-icons/fi';

const emptyForm = {
  employeeNumber: '', firstName: '', lastName: '', email: '', phone: '',
  startDate: '', payBasis: 'salary', salaryAmount: '', hourlyRate: '',
  payFrequency: 'monthly', workingHoursPerWeek: '',
};

const Employees = () => {
  const { success, error: showError } = useToast();
  const [employees, setEmployees] = useState([]);
  const [orgUsers, setOrgUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(null);

  const [linkingEmployeeId, setLinkingEmployeeId] = useState(null);
  const [linkUserId, setLinkUserId] = useState('');

  const loadEmployees = () => {
    setLoading(true);
    Promise.all([
      apiClient.get('/hr/employees'),
      apiClient.get('/organisations/current/users').catch(() => ({ data: { data: [] } })),
    ]).then(([empRes, usersRes]) => {
      setEmployees(empRes.data.data);
      setOrgUsers(usersRes.data.data);
    }).catch((err) => showError(getErrorMessage(err, 'Could not load employees')))
      .finally(() => setLoading(false));
  };

  useEffect(loadEmployees, []); // eslint-disable-line react-hooks/exhaustive-deps

  const userName = (userId) => {
    const u = orgUsers.find((x) => x.userId === userId);
    return u ? `${u.firstName} ${u.lastName}` : null;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        employeeNumber: form.employeeNumber,
        firstName: form.firstName,
        lastName: form.lastName,
        startDate: form.startDate,
        ...(form.email ? { email: form.email } : {}),
        ...(form.phone ? { phone: form.phone } : {}),
        ...(form.payFrequency ? { payFrequency: form.payFrequency } : {}),
        ...(form.workingHoursPerWeek ? { workingHoursPerWeek: form.workingHoursPerWeek } : {}),
        ...(form.payBasis === 'salary' && form.salaryAmount ? { salaryAmount: form.salaryAmount } : {}),
        ...(form.payBasis === 'hourly' && form.hourlyRate ? { hourlyRate: form.hourlyRate } : {}),
      };
      await apiClient.post('/hr/employees', payload);
      success('✅ Employee added');
      setShowAdd(false);
      setForm(emptyForm);
      loadEmployees();
    } catch (err) {
      showError(getErrorMessage(err, 'Could not add employee'));
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (emp) => {
    setEditingId(emp.id);
    setEditForm({
      firstName: emp.firstName || '',
      lastName: emp.lastName || '',
      email: emp.email || '',
      phone: emp.phone || '',
    });
  };

  const handleUpdate = async (e, employeeId) => {
    e.preventDefault();
    try {
      await apiClient.patch(`/hr/employees/${employeeId}`, editForm);
      success('✅ Employee updated');
      setEditingId(null);
      loadEmployees();
    } catch (err) {
      showError(getErrorMessage(err, 'Could not update employee'));
    }
  };

  const handleArchive = async (employeeId) => {
    if (!window.confirm('Archive this employee? They will no longer appear as active.')) return;
    try {
      await apiClient.delete(`/hr/employees/${employeeId}`);
      success('✅ Employee archived');
      loadEmployees();
    } catch (err) {
      showError(getErrorMessage(err, 'Could not archive employee'));
    }
  };

  const startLinkUser = (emp) => {
    setLinkingEmployeeId(emp.id);
    setLinkUserId(emp.userId || '');
  };

  const handleLinkUser = async (e, employeeId) => {
    e.preventDefault();
    try {
      await apiClient.patch(`/hr/employees/${employeeId}/link-user`, { userId: linkUserId });
      success('✅ User account linked — they can now use self-service');
      setLinkingEmployeeId(null);
      loadEmployees();
    } catch (err) {
      showError(getErrorMessage(err, 'Could not link user account'));
    }
  };

  return (
    <motion.div className="dashboard-page" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}>
      <div className="page-header">
        <h1>Employees</h1>
        <p>Manage your team and their pay details</p>
      </div>

      <section className="page-section">
        <div className="section-header">
          <h2>All Employees</h2>
          <button
            className="btn btn-primary"
            style={{ width: 'auto', flexShrink: 0, whiteSpace: 'nowrap' }}
            onClick={() => setShowAdd((s) => !s)}
          >
            <FiPlus size={18} />
            {showAdd ? 'Cancel' : 'Add Employee'}
          </button>
        </div>

        {showAdd && (
          <form onSubmit={handleAdd} className="form-grid" style={{ marginBottom: '1.5rem' }}>
            <div className="form-field">
              <label>Employee Number *</label>
              <input name="employeeNumber" required value={form.employeeNumber} onChange={handleChange} placeholder="E001" />
            </div>
            <div className="form-field">
              <label>First Name *</label>
              <input name="firstName" required value={form.firstName} onChange={handleChange} />
            </div>
            <div className="form-field">
              <label>Last Name *</label>
              <input name="lastName" required value={form.lastName} onChange={handleChange} />
            </div>
            <div className="form-field">
              <label>Start Date *</label>
              <input type="date" name="startDate" required value={form.startDate} onChange={handleChange} />
            </div>
            <div className="form-field">
              <label>Email</label>
              <input type="email" name="email" value={form.email} onChange={handleChange} />
            </div>
            <div className="form-field">
              <label>Phone</label>
              <input name="phone" value={form.phone} onChange={handleChange} />
            </div>

            <div className="form-field">
              <label>Pay Basis</label>
              <select name="payBasis" value={form.payBasis} onChange={handleChange}>
                <option value="salary">Annual Salary</option>
                <option value="hourly">Hourly Rate</option>
              </select>
            </div>

            {form.payBasis === 'salary' ? (
              <div className="form-field">
                <label>Annual Salary (£)</label>
                <input type="number" step="0.01" name="salaryAmount" value={form.salaryAmount} onChange={handleChange} placeholder="35000" />
              </div>
            ) : (
              <div className="form-field">
                <label>Hourly Rate (£)</label>
                <input type="number" step="0.01" name="hourlyRate" value={form.hourlyRate} onChange={handleChange} placeholder="15.50" />
              </div>
            )}

            <div className="form-field">
              <label>Pay Frequency</label>
              <select name="payFrequency" value={form.payFrequency} onChange={handleChange}>
                <option value="monthly">Monthly</option>
                <option value="fortnightly">Fortnightly</option>
                <option value="weekly">Weekly</option>
              </select>
            </div>
            <div className="form-field">
              <label>Working Hours / Week{form.payBasis === 'hourly' ? ' *' : ''}</label>
              <input
                type="number" step="0.5" name="workingHoursPerWeek"
                required={form.payBasis === 'hourly'}
                value={form.workingHoursPerWeek} onChange={handleChange} placeholder="37.5"
              />
              {form.payBasis === 'hourly' && (
                <p style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: '0.3rem' }}>
                  Used to calculate pay each period (contracted hours × hourly rate).
                </p>
              )}
            </div>

            <div className="form-field full-width form-actions">
              <button type="submit" className="btn btn-primary" disabled={saving} style={{ width: 'auto' }}>
                {saving ? 'Adding...' : 'Add Employee'}
              </button>
            </div>
          </form>
        )}

        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Employee #</th>
                <th>Name</th>
                <th>Email</th>
                <th>Pay</th>
                <th>Frequency</th>
                <th>Start Date</th>
                <th>Linked User</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} style={{ textAlign: 'center', padding: '2rem' }}>Loading...</td></tr>
              ) : employees.length === 0 ? (
                <tr><td colSpan={8} style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
                  <FiUser size={24} style={{ marginBottom: '0.5rem' }} /><br />No employees yet
                </td></tr>
              ) : employees.map((emp) => (
                <tr key={emp.id}>
                  {editingId === emp.id ? (
                    <td colSpan={8}>
                      <form onSubmit={(e) => handleUpdate(e, emp.id)} className="form-grid" style={{ padding: '1rem 0' }}>
                        <div className="form-field">
                          <label>First Name</label>
                          <input required value={editForm.firstName} onChange={(e) => setEditForm(p => ({ ...p, firstName: e.target.value }))} />
                        </div>
                        <div className="form-field">
                          <label>Last Name</label>
                          <input required value={editForm.lastName} onChange={(e) => setEditForm(p => ({ ...p, lastName: e.target.value }))} />
                        </div>
                        <div className="form-field">
                          <label>Email</label>
                          <input type="email" value={editForm.email} onChange={(e) => setEditForm(p => ({ ...p, email: e.target.value }))} />
                        </div>
                        <div className="form-field">
                          <label>Phone</label>
                          <input value={editForm.phone} onChange={(e) => setEditForm(p => ({ ...p, phone: e.target.value }))} />
                        </div>
                        <div className="form-field full-width form-actions">
                          <button type="submit" className="btn btn-primary" style={{ width: 'auto' }}>
                            <FiSave size={14} /> Save
                          </button>
                          <button type="button" className="btn btn-outline" style={{ width: 'auto' }} onClick={() => setEditingId(null)}>
                            <FiX size={14} /> Cancel
                          </button>
                        </div>
                      </form>
                    </td>
                  ) : linkingEmployeeId === emp.id ? (
                    <td colSpan={8}>
                      <form onSubmit={(e) => handleLinkUser(e, emp.id)} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', padding: '1rem 0' }}>
                        <label style={{ margin: 0, textTransform: 'none', fontWeight: 400 }}>Link to user account:</label>
                        <select required value={linkUserId} onChange={(e) => setLinkUserId(e.target.value)} style={{ padding: '0.4rem' }}>
                          <option value="">-- select --</option>
                          {orgUsers.map((u) => <option key={u.userId} value={u.userId}>{u.firstName} {u.lastName} ({u.email})</option>)}
                        </select>
                        <button type="submit" className="btn btn-primary" style={{ width: 'auto' }}>
                          <FiSave size={14} /> Link
                        </button>
                        <button type="button" className="btn btn-outline" style={{ width: 'auto' }} onClick={() => setLinkingEmployeeId(null)}>
                          <FiX size={14} /> Cancel
                        </button>
                      </form>
                    </td>
                  ) : (
                    <>
                      <td>{emp.employeeNumber}</td>
                      <td style={{ fontWeight: 600 }}>{emp.firstName} {emp.lastName}</td>
                      <td>{emp.email || '—'}</td>
                      <td>
                        {emp.currentEmployment?.salaryAmount
                          ? `£${Number(emp.currentEmployment.salaryAmount).toLocaleString()}/yr`
                          : emp.currentEmployment?.hourlyRate
                            ? `£${Number(emp.currentEmployment.hourlyRate).toFixed(2)}/hr`
                            : '—'}
                      </td>
                      <td style={{ textTransform: 'capitalize' }}>{emp.currentEmployment?.payFrequency || '—'}</td>
                      <td>{emp.currentEmployment?.startDate ? new Date(emp.currentEmployment.startDate).toLocaleDateString() : '—'}</td>
                      <td>{emp.userId ? (userName(emp.userId) || 'Linked') : <span style={{ color: '#9ca3af' }}>Not linked</span>}</td>
                      <td>
                        <div className="actions">
                          <button className="action-icon edit" title="Edit" onClick={() => startEdit(emp)}>
                            <FiEdit2 size={16} />
                          </button>
                          <button className="action-icon edit" title={emp.userId ? 'Relink user account' : 'Link user account'} onClick={() => startLinkUser(emp)}>
                            <FiLink size={16} />
                          </button>
                          <button className="action-icon delete" title="Archive" onClick={() => handleArchive(emp.id)}>
                            <FiTrash2 size={16} />
                          </button>
                          <Link to={`/dashboard/employees/${emp.id}/payslips`} className="action-icon edit" title="View Payslips">
                            <FiFileText size={16} />
                          </Link>
                          <Link to={`/dashboard/employees/${emp.id}/onboarding`} className="action-icon edit" title="Onboarding">
                            <FiCheckSquare size={16} />
                          </Link>
                        </div>
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </motion.div>
  );
};

export default Employees;